/**
 * Theme Configuration
 * 
 * Creates the MUI theme for the application.
 * Uses the MUI adapter layer for version-agnostic theme creation.
 * 
 * Colors are automatically extracted from variables.scss via Vite plugin.
 * variables.scss is the single source of truth - update colors there only.
 */

import { createTheme } from "./mui";
import { colors } from "virtual:scss-vars";

/**
 * Application theme
 * 
 * This theme is created using the MUI adapter layer.
 * All MUI-specific code is abstracted in src/styles/mui/
 * Colors are extracted from variables.scss at build time
 */
export const theme = createTheme({
  palette: {
    primary: {
      main: colors["primary-color"],
      light: colors["primary-light"],
      dark: colors["primary-dark"],
      contrastText: colors["primary-contrastText"],
    },
    secondary: {
      main: colors["secondary-color"],
      light: colors["secondary-light"],
      dark: colors["secondary-dark"],
      contrastText: colors["secondary-contrastText"],
    },
    background: {
      default: "#F0F7F9", // Not in variables.scss, keep as is
      paper: colors["bg-primary"],
    },
    text: {
      primary: colors["text-primary"],
      secondary: colors["text-secondary"],
    },
    success: {
      main: colors["success-color"],
    },
    error: {
      main: colors["error-color"],
      light: colors["error-hover"],
      dark: colors["error-dark"],
      contrastText: colors["text-white"],
    },
    warning: {
      main: colors["warning-color"],
    },
    info: {
      main: colors["info-color"],
    },
  },
  typography: {
    fontFamily: '"Geist", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    // All variants use Design Aviro360 Typography (node 590-45)
    h1: { fontFamily: '"Geist", sans-serif' },
    h2: { fontFamily: '"Geist", sans-serif' },
    h3: { fontFamily: '"Geist", sans-serif' },
    h4: { fontFamily: '"Geist", sans-serif' },
    // Design: Heading / Page header – Geist Medium 26pt, line-height 120%, letter-spacing -3%
    h5: {
      fontFamily: '"Geist", sans-serif',
      fontWeight: 500,
      fontSize: "1.625rem",
      lineHeight: 1.2,
      letterSpacing: "-0.03em",
    },
    // Design: Heading / Sub header – Geist Bold 16pt, line-height 120%, letter-spacing -3%
    h6: {
      fontFamily: '"Geist", sans-serif',
      fontWeight: 700,
      fontSize: "1rem",
      lineHeight: 1.2,
      letterSpacing: "-0.03em",
    },
    // Design: Body / Large – Geist Regular 16pt, line-height 24px
    subtitle1: {
      fontFamily: '"Geist", sans-serif',
      fontWeight: 400,
      fontSize: "1rem",
      lineHeight: 1.5,
      letterSpacing: 0,
    },
    // Design: Body / Base – Geist Regular 14pt, line-height 22px
    body1: {
      fontFamily: '"Geist", sans-serif',
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: 1.5714286,
      letterSpacing: 0,
    },
    // Design: Body / Small – Geist Regular 12pt, line-height 21px, letter-spacing -3%
    body2: {
      fontFamily: '"Geist", sans-serif',
      fontWeight: 400,
      fontSize: "0.75rem",
      lineHeight: 1.75,
      letterSpacing: "-0.03em",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Geist", sans-serif',
        },
      },
    },
  },
});

/**
 * Type export for theme
 */
export type { Theme } from "./mui";
